/**
 * Pricing Page
 * Public pricing page displaying all available plans
 */

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, X } from 'lucide-react';
import PricingCard from '../components/auth/PricingCard';
import { subscriptionService, DEFAULT_PLANS } from '../services/subscriptionService';
import { useAuth } from '../services/AuthContext';
import type { PricingPlan } from '../types/subscription';

export default function PricingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    loadPricingPlans();
  }, []);

  const [userSubscription, setUserSubscription] = useState<any>(null);

  useEffect(() => {
    if (user?.email) {
      subscriptionService.getUserSubscription(user.email).then(setUserSubscription);
    }
  }, [user]);

  const loadPricingPlans = async () => {
    try {
      let fetchedPlans = await subscriptionService.getPricingPlans();
      if (!fetchedPlans || fetchedPlans.length === 0) {
        fetchedPlans = DEFAULT_PLANS;
      }
      // Show all plans including free
      setPlans(fetchedPlans);
    } catch (error) {
      console.error('Error loading pricing plans:', error);
      setPlans(DEFAULT_PLANS);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = async (planId: string) => {
    const selectedPlan = plans.find(p => p.planId === planId);
    if (!selectedPlan) return;

    // If user is logged in, start checkout directly
    if (user) {
      setCheckoutLoading(true);
      try {
        const checkoutUrl = await subscriptionService.createCheckoutSession({
          priceId: selectedPlan.stripePriceId || 'price_1QiaXo8Ujq4NI5JCyGrTt4QK',
          successUrl: `${window.location.origin}/dashboard?upgrade=success`,
          cancelUrl: `${window.location.origin}/pricing?upgrade=cancelled`,
          mode: 'subscription',
          metadata: {
            planId: planId
          }
        });
        // Redirect to Stripe Checkout
        window.location.href = checkoutUrl;
      } catch (error) {
        console.error('Error creating checkout:', error);
        alert('Failed to start checkout. Please try again.');
        setCheckoutLoading(false);
      }
    } else {
      // Not logged in - redirect to registration with selected plan
      navigate(`/register?plan=${planId}`);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center">
              <img
                className="h-10 w-auto"
                src="/logo.jpg"
                alt="CanvasEstate logo"
              />
              <div className="ml-2 flex flex-col">
                <span className="text-xl font-bold text-brand-secondary leading-none">CanvasEstate</span>
                <span className="text-[0.6rem] text-brand-primary font-bold uppercase tracking-wider">Let's Canvas Your Estate</span>
              </div>
            </Link>
            <div className="flex items-center gap-4">
              {user ? (
                <Link
                  to="/dashboard"
                  className="px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Back to Dashboard
                </Link>
              ) : (
                <>
                  <Link to="/login" className="text-sm text-gray-600 hover:text-gray-900">
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-lg transition-colors"
                  >
                    Get Started Free
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            Free for personal use. Pay only when you need more.
          </p>

          {/* Billing Toggle */}
          <div className="inline-flex items-center gap-3 p-1 bg-white rounded-lg border border-gray-300">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${billingPeriod === 'monthly'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('annual')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${billingPeriod === 'annual'
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Annual
              <span className="ml-2 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                Save 17%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : checkoutLoading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Starting checkout...</p>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            {plans.map((plan) => (
              <PricingCard
                key={plan.planId}
                plan={plan}
                onSelect={() => plan.planId === 'free' ? navigate('/register') : handleSelectPlan(plan.planId)}
                ctaText={
                  plan.planId === 'free'
                    ? (user ? 'Current Plan' : 'Sign Up Free')
                    : user
                      ? userSubscription?.planId === plan.planId
                        ? 'Current Plan'
                        : 'Upgrade Now'
                      : 'Sign Up Free'
                }
                isSelected={user ? userSubscription?.planId === plan.planId : false}
              />
            ))}
          </div>
        )}

        {/* Feature Comparison Table */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Compare Features
          </h2>

          <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">
                      Feature
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                      Free
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-blue-600">
                      Commercial
                    </th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">
                      Enterprise
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <FeatureRow3
                    feature="Properties"
                    free="Up to 10"
                    commercial="Unlimited"
                    enterprise="30 included, then $3 each"
                  />
                  <FeatureRow3
                    feature="AI Analysis"
                    free={true}
                    commercial={true}
                    enterprise={true}
                  />
                  <FeatureRow3
                    feature="Document Storage"
                    free="1GB"
                    commercial="5GB"
                    enterprise="100GB"
                  />
                  <FeatureRow3
                    feature="Historical Data"
                    free={true}
                    commercial={true}
                    enterprise={true}
                  />
                  <FeatureRow3
                    feature="CSV Export"
                    free={true}
                    commercial={true}
                    enterprise={true}
                  />
                  <FeatureRow3
                    feature="Team Members"
                    free="1 user"
                    commercial="1 user"
                    enterprise="Unlimited"
                  />
                  <FeatureRow3
                    feature="Priority Support"
                    free={false}
                    commercial={true}
                    enterprise="24h response"
                  />
                  <FeatureRow3
                    feature="API Access"
                    free={false}
                    commercial={false}
                    enterprise={true}
                  />
                  <FeatureRow3
                    feature="Custom Integrations"
                    free={false}
                    commercial={false}
                    enterprise={true}
                  />
                  <FeatureRow3
                    feature="Dedicated Account Manager"
                    free={false}
                    commercial={false}
                    enterprise={true}
                  />
                  {/* Action Row */}
                  <tr className="bg-gray-50/50">
                    <td className="px-6 py-6 text-sm font-medium text-gray-900">
                      Ready to start?
                    </td>
                    <td className="px-6 py-6 text-center">
                      <button
                        onClick={() => user ? undefined : navigate('/register')}
                        disabled={!!user}
                        className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all ${user
                          ? 'bg-gray-100 text-gray-500 cursor-default'
                          : 'bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg'
                          }`}
                      >
                        {user ? 'Current' : 'Sign Up Free'}
                      </button>
                    </td>
                    <td className="px-6 py-6 text-center">
                      <button
                        onClick={() => handleSelectPlan('base')}
                        disabled={checkoutLoading || userSubscription?.planId === 'base'}
                        className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all ${userSubscription?.planId === 'base'
                          ? 'bg-gray-100 text-gray-500 cursor-default'
                          : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                          }`}
                      >
                        {userSubscription?.planId === 'base' ? 'Current Plan' : user ? 'Upgrade Now' : 'Sign Up Free'}
                      </button>
                    </td>
                    <td className="px-6 py-6 text-center">
                      <button
                        onClick={() => handleSelectPlan('enterprise')}
                        disabled={checkoutLoading || userSubscription?.planId === 'enterprise'}
                        className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all ${userSubscription?.planId === 'enterprise'
                          ? 'bg-gray-100 text-gray-500 cursor-default'
                          : 'bg-gray-900 text-white hover:bg-black shadow-md hover:shadow-lg'
                          }`}
                      >
                        {userSubscription?.planId === 'enterprise' ? 'Current Plan' : user ? 'Upgrade Now' : 'Sign Up Free'}
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-20 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <FAQItem
              question="Is the free plan really free?"
              answer="Yes! The Free plan lets you manage up to 10 properties at no cost, forever. No credit card required. You only need to upgrade if you manage 11 or more properties."
            />
            <FAQItem
              question="Can I cancel anytime?"
              answer="Yes! There are no long-term contracts. You can cancel your subscription anytime, and you'll have access until the end of your current billing period. You can always keep using the free plan."
            />
            <FAQItem
              question="What happens if I exceed 10 properties?"
              answer="When you add your 11th property, you'll be prompted to upgrade to the Commercial plan ($49/month). All your existing data is preserved and you can continue where you left off."
            />
            <FAQItem
              question="Do you charge GST?"
              answer="Yes, all prices include 10% GST for Australian customers, as required by law."
            />
            <FAQItem
              question="Can I upgrade or downgrade my plan?"
              answer="Absolutely! You can change your plan anytime. When upgrading, you'll be charged a prorated amount. When downgrading below 10 properties, you can switch back to the free plan."
            />
            <FAQItem
              question="What payment methods do you accept?"
              answer="We accept all major credit cards (Visa, Mastercard, American Express) through our secure payment processor, Stripe."
            />
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-20 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            {user ? 'Ready to Scale Up?' : 'Ready to Get Started?'}
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            {user
              ? 'Upgrade your plan to unlock unlimited properties and premium features.'
              : 'Start managing your property portfolio for free. No credit card required.'}
          </p>
          <button
            onClick={() => {
              if (user) {
                // Scroll to plans
                window.scrollTo({ top: 400, behavior: 'smooth' });
              } else {
                navigate('/register');
              }
            }}
            className="inline-block px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors"
          >
            {user ? 'View Plans' : 'Get Started Free'}
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-12 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-sm text-gray-500">
            <p>
              © 2025 CanvasEstate. All rights reserved. |{' '}
              <Link to="/terms" className="hover:text-gray-700">Terms</Link> |{' '}
              <Link to="/privacy" className="hover:text-gray-700">Privacy</Link>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Helper Components
interface FeatureRow3Props {
  feature: string;
  free: boolean | string;
  commercial: boolean | string;
  enterprise: boolean | string;
}

function FeatureRow3({ feature, free, commercial, enterprise }: FeatureRow3Props) {
  const renderCell = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className="w-5 h-5 text-green-600 mx-auto" />
      ) : (
        <X className="w-5 h-5 text-gray-300 mx-auto" />
      );
    }
    return <span className="text-sm text-gray-900">{value}</span>;
  };

  return (
    <tr>
      <td className="px-6 py-4 text-sm text-gray-900">{feature}</td>
      <td className="px-6 py-4 text-center">{renderCell(free)}</td>
      <td className="px-6 py-4 text-center">{renderCell(commercial)}</td>
      <td className="px-6 py-4 text-center">{renderCell(enterprise)}</td>
    </tr>
  );
}

interface FAQItemProps {
  question: string;
  answer: string;
}

function FAQItem({ question, answer }: FAQItemProps) {
  return (
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <h3 className="font-semibold text-gray-900 mb-2">{question}</h3>
      <p className="text-gray-600 text-sm">{answer}</p>
    </div>
  );
}
